import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  XCircle,
  Ban,
  PackagePlus,
  MapPin,
  Calendar,
  AlertTriangle,
  Phone,
  User,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent, Badge } from '@components/Card';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { CancelOrderDialog } from '@components/CancelOrderDialog';
import { formatDateBR, formatDateTimeBR } from '@pages/financial/financialUtils';
import { formatCepDisplay } from '@/utils/addressCepCheck';
import { goodsDeliveryOrderService } from '../../services/goodsDeliveryOrderService';
import { GoodsDeliveryOrder } from '../../types/goodsDeliveryOrder';
import {
  canDeleteDeliveryOrder,
  DELIVERY_ORDER_STATUS_LABELS,
  deliveryOrderStatus,
  deliveryOrderStatusBadge,
  formatQuantity,
  isDeliveryOrderOpen,
} from './goodsDeliveryOrderUtils';

const dash = (value: string | null | undefined) => (value && value.trim() ? value : '—');

/**
 * Fotos do comprovante: baixadas com a sessão autenticada e exibidas por object URL
 * (revogadas ao desmontar ou trocar de ordem).
 */
const useDeliveryPhotos = (order: GoodsDeliveryOrder | null) => {
  const [urls, setUrls] = useState<{ 1?: string; 2?: string }>({});
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const orderId = order?.goods_delivery_orders_id;
  const has1 = !!order?.has_photo_1;
  const has2 = !!order?.has_photo_2;

  useEffect(() => {
    if (!orderId || (!has1 && !has2)) {
      setUrls({});
      return;
    }
    let cancelled = false;
    const created: string[] = [];
    setLoading(true);
    setFailed(false);
    (async () => {
      const fetchOne = async (idx: 1 | 2) => {
        try {
          const url = await goodsDeliveryOrderService.getDeliveryPhotoObjectUrl(orderId, idx);
          if (cancelled) {
            // Chegou depois do cleanup: não fica pendurada na memória.
            URL.revokeObjectURL(url);
            return undefined;
          }
          created.push(url);
          return url;
        } catch {
          if (!cancelled) setFailed(true);
          return undefined;
        }
      };
      const [u1, u2] = await Promise.all([has1 ? fetchOne(1) : undefined, has2 ? fetchOne(2) : undefined]);
      if (cancelled) return;
      setUrls({ 1: u1, 2: u2 });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [orderId, has1, has2]);

  return { urls, loading, failed };
};

export const GoodsDeliveryOrderDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [order, setOrder] = useState<GoodsDeliveryOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const photos = useDeliveryPhotos(order);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await goodsDeliveryOrderService.getOrderById(parseInt(id, 10));
        if (!cancelled) setOrder(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erro ao carregar ordem de entrega');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleDelete = async () => {
    if (!order) return;
    try {
      setDeleting(true);
      await goodsDeliveryOrderService.deleteOrder(order.goods_delivery_orders_id);
      navigate('/goods-delivery-orders');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Erro ao excluir');
      setShowDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (!order) return;
    const updated = await goodsDeliveryOrderService.cancelOrder(order.goods_delivery_orders_id, reason);
    setOrder(updated);
    setShowCancelDialog(false);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-slate-500">Carregando...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="text-center py-12">
        <PackagePlus className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error || 'Ordem não encontrada'}</p>
        <Button variant="outline" onClick={() => navigate('/goods-delivery-orders')}>
          Voltar
        </Button>
      </div>
    );
  }

  const status = deliveryOrderStatus(order);
  const open = isDeliveryOrderOpen(order);
  const hasReceipt = order.is_delivered || !!order.delivery_contact || order.has_photo_1 || order.has_photo_2;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" icon={<ArrowLeft size={20} />} onClick={() => navigate('/goods-delivery-orders')}>
            Voltar
          </Button>
          <div>
            <h1 className="text-h1">Ordem de Entrega #{order.goods_delivery_orders_id}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge variant={deliveryOrderStatusBadge(status)}>{DELIVERY_ORDER_STATUS_LABELS[status]}</Badge>
              {order.is_emergency && (
                <Badge variant="danger" className="gap-1">
                  <AlertTriangle size={12} /> Emergência
                </Badge>
              )}
              {order.is_delivered && order.delivered_at && (
                <span className="text-sm text-slate-500">Entregue em {formatDateTimeBR(order.delivered_at)}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {open && (
            <>
              <PermissionGuard permission="goods_delivery_orders.write">
                <Button
                  variant="outline"
                  icon={<Edit size={18} />}
                  onClick={() => navigate(`/goods-delivery-orders/${order.goods_delivery_orders_id}/edit`)}
                >
                  Editar
                </Button>
              </PermissionGuard>
              <PermissionGuard permission="goods_delivery_orders.write">
                <Button
                  variant="outline"
                  icon={<XCircle size={18} />}
                  onClick={() => setShowCancelDialog(true)}
                  className="text-amber-600"
                >
                  Cancelar ordem
                </Button>
              </PermissionGuard>
            </>
          )}
          {canDeleteDeliveryOrder(order) && (
            <PermissionGuard permission="goods_delivery_orders.delete">
              <Button variant="outline" icon={<Trash2 size={18} />} onClick={() => setShowDeleteDialog(true)} className="text-red-500">
                Excluir
              </Button>
            </PermissionGuard>
          )}
        </div>
      </div>

      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{actionError}</div>
      )}

      {/* Cancelamento */}
      {order.is_canceled && (
        <div className="flex items-start gap-3 rounded-lg border border-slate-300 bg-slate-100 px-4 py-3">
          <Ban size={20} className="mt-0.5 shrink-0 text-slate-600" />
          <div className="text-sm text-slate-700">
            <p className="font-semibold text-slate-900">Ordem cancelada</p>
            <p className="mt-1">
              <span className="font-medium">Motivo:</span> {dash(order.cancel_reason)}
            </p>
            <p className="mt-0.5 text-slate-600">
              Cancelada por {dash(order.canceled_by)} em {formatDateTimeBR(order.canceled_at) || '—'}
            </p>
          </div>
        </div>
      )}

      {/* Dados */}
      <Card>
        <CardHeader title="Dados da Entrega" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <p className="text-sm font-medium text-slate-500">Descrição</p>
              <p className="text-slate-900 mt-1 whitespace-pre-line">{order.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-slate-400" />
              <div>
                <p className="text-sm font-medium text-slate-500">Data prevista</p>
                <p className="text-slate-900 mt-1">{formatDateBR(order.expected_delivery_date) || '—'}</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Quantidade de volumes</p>
              <p className="text-slate-900 mt-1">{order.volume_quantity ?? '—'}</p>
            </div>
            {order.observation && (
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-slate-500">Observação</p>
                <p className="text-slate-900 mt-1 whitespace-pre-line">{order.observation}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Destinatário + endereço */}
      <Card>
        <CardHeader title="Destinatário e Endereço de Entrega" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-slate-900">
                <User size={16} className="text-slate-400" />
                <span className="font-medium">{order.recipient_name}</span>
              </p>
              <p className="flex items-center gap-2 text-sm text-slate-600">
                <Phone size={16} className="text-slate-400" /> {dash(order.recipient_phone)}
              </p>
            </div>
            <div className="flex items-start gap-2">
              <MapPin size={16} className="text-slate-400 mt-0.5" />
              <div>
                <p className="text-slate-900">
                  {order.ship_addr_street}, {order.ship_addr_num}
                  {order.ship_addr_compl ? ` — ${order.ship_addr_compl}` : ''}
                </p>
                <p className="text-sm text-slate-600">
                  {order.ship_addr_district} — {order.ship_addr_city}/{order.ship_addr_state}
                </p>
                <p className="text-sm text-slate-500">CEP: {formatCepDisplay(order.ship_addr_zip) || '—'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader title="Itens da Entrega" />
        <CardContent>
          {order.items && order.items.length > 0 ? (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-medium text-slate-600">Descrição</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-slate-600">Quantidade</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-slate-600">Unidade</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {order.items.map((item) => (
                    <tr key={item.goods_delivery_orders_item_id}>
                      <td className="px-4 py-2 text-sm text-slate-700">{item.description}</td>
                      <td className="px-4 py-2 text-right text-sm">{formatQuantity(item.quantity)}</td>
                      <td className="px-4 py-2 text-sm text-slate-600">{dash(item.unit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Nenhum item informado.</p>
          )}
        </CardContent>
      </Card>

      {/* Recebimento */}
      <Card>
        <CardHeader title="Recebimento" />
        <CardContent>
          {!hasReceipt ? (
            <p className="text-sm text-slate-500">
              {order.is_canceled
                ? 'Ordem cancelada — sem recebimento.'
                : 'Ainda não entregue. O motorista registra quem recebeu e as fotos ao confirmar a entrega.'}
            </p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Recebido por</p>
                  <p className="text-slate-900 font-medium">{dash(order.delivery_contact)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Documento</p>
                  <p className="text-slate-900">{dash(order.delivery_contact_document)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Entregue em</p>
                  <p className="flex items-center gap-1 text-slate-900">
                    {order.delivered_at && <CheckCircle size={14} className="text-emerald-500" />}
                    {formatDateTimeBR(order.delivered_at) || '—'}
                  </p>
                </div>
              </div>

              {(order.has_photo_1 || order.has_photo_2) && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Fotos do comprovante</p>
                  {photos.loading ? (
                    <p className="flex items-center gap-2 text-sm text-slate-500">
                      <Loader2 size={14} className="animate-spin" /> Carregando fotos...
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 max-w-lg">
                      {([1, 2] as const).map((idx) => {
                        const url = photos.urls[idx];
                        if (!url) return null;
                        return (
                          <a key={idx} href={url} target="_blank" rel="noreferrer" title="Abrir em tamanho real">
                            <img
                              src={url}
                              alt={`Comprovante ${idx}`}
                              className="w-full h-48 object-cover rounded-lg border border-slate-200 hover:opacity-90"
                            />
                          </a>
                        );
                      })}
                    </div>
                  )}
                  {photos.failed && (
                    <p className="text-xs text-amber-700 mt-2">Não foi possível carregar uma ou mais fotos do comprovante.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Metadados */}
      <Card>
        <CardHeader title="Informações do Registro" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Solicitante</p>
              <p className="text-slate-900">{order.requestor_email || order.created_by || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500">Criado em</p>
              <p className="text-slate-900">{formatDateTimeBR(order.created_at) || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500">Atualizado em</p>
              <p className="text-slate-900">{formatDateTimeBR(order.updated_at) || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500">Última atualização por</p>
              <p className="text-slate-900">{dash(order.last_updated_by)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Excluir Ordem de Entrega"
        message={`Deseja realmente excluir a ordem #${order.goods_delivery_orders_id}? A parada ainda não entregue também sai da rota. Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="danger"
        loading={deleting}
      />

      <CancelOrderDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancel}
        title="Cancelar Ordem de Entrega"
        subject={`a ordem de entrega #${order.goods_delivery_orders_id}`}
        routeWarning="Se estiver em uma rota ainda não entregue, a parada será removida da rota."
      />
    </div>
  );
};
